import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "@alight/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@alight/ui";
import { Mail } from "lucide-react";

const api = apiClient("");

const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * Passwordless sign-in / register dialog for the site nav — the same
 * magic-link journey as /enrol step 1: email → one-time link → this dialog
 * polls /api/auth/me so it closes the moment the link is clicked (any tab).
 * There is no separate registration: a first-time email simply gets a link
 * and a session, same as a returning parent.
 */
export function SignInDialog({
  open,
  onOpenChange,
  redirect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirect: string;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [devLink, setDevLink] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  // While the link is out, poll for the session so the dialog resolves the
  // moment the emailed link is clicked — it may be opened in another tab.
  React.useEffect(() => {
    if (!open || !sent) return;
    const iv = setInterval(() => {
      void api
        .authMe()
        .then((r) => {
          if (r.email) {
            void qc.invalidateQueries({ queryKey: ["auth", "me"] });
            onOpenChange(false);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(iv);
  }, [open, sent, qc, onOpenChange]);

  async function request() {
    setError(null);
    setBusy(true);
    try {
      const r = await api.requestLink({ email: email.trim().toLowerCase(), redirect });
      setSent(true);
      setDevLink(r.devLink ?? null);
      if (!r.emailed && !r.devLink) {
        setError("We couldn't send the email just now — please try again in a moment.");
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many links requested — check your inbox (and spam), or try again shortly."
          : "We couldn't send the sign-in link. Please check the address and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sign in or register</DialogTitle>
          <DialogDescription>
            No password needed — enter your email and we&apos;ll send a one-time sign-in link.
            First time here? The same link sets up your account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="nav-signin-email">Email</Label>
          <Input
            id="nav-signin-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            autoFocus
            onChange={(e) => {
              setEmail(e.target.value);
              setSent(false);
              setDevLink(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && EMAIL_RE.test(email) && !busy && !sent) void request();
            }}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm font-medium text-accent">
            {error}
          </p>
        )}
        {!sent ? (
          <Button
            className="w-full"
            size="lg"
            disabled={!EMAIL_RE.test(email) || busy}
            onClick={() => void request()}
          >
            <Mail className="h-4 w-4" />
            {busy ? "Sending…" : "Email me a sign-in link"}
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="rounded-card border border-dashed border-border bg-paper px-4 py-3 text-sm text-muted">
              Sign-in link sent to <strong className="text-ink">{email}</strong> — click it and
              you&apos;ll be signed in here automatically.
            </div>
            {devLink && (
              <Button asChild variant="outline" size="sm" className="w-full">
                <a href={devLink}>(Dev) Open the sign-in link</a>
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
